import { isValidComponent } from '../../../shared/islands/is-valid-component.mjs';

import { islandLoaders } from './manifest';

/**
 * @typedef {object} LoadedIslandModule
 * @property {import('react').ComponentType<any>} component
 * @property {((context: {
 *   mountElement: Element,
 *   props: Record<string, unknown>,
 * }) => { forceClientRender?: boolean, props?: Record<string, unknown> } | undefined) | null} prepareMount
 */

/**
 * @param {string} islandId
 * @param {Record<string, () => Promise<{ default?: unknown, prepareMount?: unknown }>>} loaders
 * @returns {Promise<LoadedIslandModule | null>}
 */
export async function loadIslandModuleFromLoaders(islandId, loaders) {
	const loadIslandModuleFn = loaders[islandId];
	if (typeof loadIslandModuleFn !== 'function') {
		return null;
	}

	const islandModule = await loadIslandModuleFn();
	const Component = islandModule.default;
	if (!isValidComponent(Component)) {
		throw new Error(`Island "${islandId}" default export is not a valid React component`);
	}

	const prepareMount = typeof islandModule.prepareMount === 'function'
		? islandModule.prepareMount
		: null;

	return { component: Component, prepareMount };
}

/**
 * @param {string} islandId
 * @param {Record<string, () => Promise<{ default?: unknown }>>} loaders
 * @returns {Promise<import('react').ComponentType<any> | null>}
 */
export async function loadIslandComponentFromLoaders(islandId, loaders) {
	const loadedIsland = await loadIslandModuleFromLoaders(islandId, loaders);

	return loadedIsland?.component ?? null;
}

/**
 * @param {string} islandId
 * @returns {Promise<LoadedIslandModule | null>}
 */
export async function loadIslandModule(islandId) {
	return loadIslandModuleFromLoaders(islandId, islandLoaders);
}

/**
 * @param {string} islandId
 * @returns {Promise<import('react').ComponentType<any> | null>}
 */
export async function loadIslandComponent(islandId) {
	return loadIslandComponentFromLoaders(islandId, islandLoaders);
}
